// client/src/pages/secure/DashboardOverview.js
import React, { useState, useEffect } from 'react';
import TodayPanel from '../../components/secure/TodayPanel';
import { useApiClient } from '../../hooks/useApiClient';
import TrainingCard from '../../components/secure/training/TrainingCard';
import CollapsibleCard, { ExpandCollapseAll } from '../../components/shared/CollapsibleCard';
import PageHeader from '../../components/shared/PageHeader';
import AgentHome from '../../components/secure/AgentHome';
import { isAgentLogin } from '../../components/secure/navConfig';
import { getCurrentUser } from '../../hooks/useCurrentUser';

const StatCard = ({ title, value, loading }) => (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
        <h3 className="text-sm font-medium text-slate-500">{title}</h3>
        {loading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse mt-1"></div>
        ) : (
            <p className="mt-1 text-3xl font-semibold text-slate-900">{value}</p>
        )}
    </div>
);

const OfficeOverview = () => {
    const [stats, setStats] = useState({ agents: 0, clients: 0, properties: 0 });
    const [loading, setLoading] = useState(true);
    const apiClient = useApiClient();

    useEffect(() => {
        const fetchStats = async () => {
            try {
                // Each count on its own: a login whose job access leaves out clients or listings
                // gets "not available" for that one, and the other counts still show.
                const count = (url) => apiClient.get(url).then((d) => (Array.isArray(d) ? d.length : 0), () => 0);
                const [agents, clients, properties] = await Promise.all([
                    count('/api/agents'), count('/api/clients'), count('/api/properties'),
                ]);
                setStats({ agents, clients, properties });
            } catch (error) {
                console.error("Failed to fetch dashboard stats:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [apiClient]);

    return (
        <div>
            <PageHeader title="Dashboard Overview" description="Office totals, what needs doing today, and your training." />
            <ExpandCollapseAll className="-mt-2 mb-3" />
            <CollapsibleCard id="office-totals" title="Office totals"
                             summary={loading ? '' : `${stats.agents} agents · ${stats.clients} clients · ${stats.properties} properties`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <StatCard title="Total Agents" value={stats.agents} loading={loading} />
                <StatCard title="Total Clients" value={stats.clients} loading={loading} />
                <StatCard title="Total Properties" value={stats.properties} loading={loading} />
            </div>
            </CollapsibleCard>
            <div className="mt-6"><TodayPanel /></div>
            <TrainingCard />
            {/* You can add charts or recent activity feeds here later */}
        </div>
    );
};

// An agent login lands on the agent home (searches first); everyone else on the office overview.
const DashboardOverview = () => {
    const user = getCurrentUser();
    return isAgentLogin(user) ? <AgentHome user={user} /> : <OfficeOverview />;
};

export default DashboardOverview;
